import { TokenIcon } from "@/components/brand/TokenIcon";
import { AnimatedNumber } from "@/components/defi/AnimatedNumber";
import type { CollateralKey } from "@/data/fixtures";
import { useGetPublicPriceFeeds } from "@/hooks/usePublicProtocol";
import { ROUTES } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const ASSETS: CollateralKey[] = ["ICP", "ckBTC", "ckETH"];

/** On-chain protocol marks only — never silent fixture placeholders. */
export function OraclePriceBand({ className }: { className?: string }) {
	const { data: priceData, isLoading } = useGetPublicPriceFeeds();
	const feeds = priceData?.feeds ?? [];
	const byAsset = new Map(feeds.map((f) => [f.asset, f]));
	const anyStale = feeds.some(
		(f) => f.stale || f.status === "stale" || f.status === "missing",
	);
	const anyLive = feeds.some((f) => f.price > 0 && f.status !== "missing");

	return (
		<div
			className={cn(
				"border-b border-border/40 bg-background/95 backdrop-blur-xl",
				className,
			)}
		>
			<div className="container mx-auto flex items-center gap-2 px-3 py-1.5 sm:px-4">
				<span className="shrink-0 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
					<span className="sm:hidden">Oracle</span>
					<span className="hidden sm:inline">Oracle · on-chain</span>
				</span>
				{anyStale ? (
					<span className="hidden shrink-0 rounded-full bg-danger/15 px-1.5 py-0.5 text-[9px] font-semibold text-danger sm:inline">
						Stale
					</span>
				) : anyLive ? (
					<span className="hidden shrink-0 rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-semibold text-primary sm:inline">
						Live
					</span>
				) : null}
				<div className="flex min-w-0 flex-1 items-center justify-center gap-1.5 overflow-x-auto scrollbar-none sm:gap-2">
					{ASSETS.map((k) => {
						const f = byAsset.get(k);
						const price = f?.price ?? 0;
						const warn = f?.stale || f?.status === "stale";
						return (
							<span
								key={k}
								className={cn(
									"inline-flex shrink-0 items-center gap-1 rounded-full border bg-card/70 px-2 py-0.5 font-mono-amount text-[10px]",
									warn
										? "border-warning/50 text-warning"
										: "border-border/50",
								)}
							>
								<TokenIcon token={k} size="sm" plain />
								<span className="font-medium">{k}</span>
								<span className={warn ? "" : "text-muted-foreground"}>
									{isLoading && price <= 0 ? (
										"…"
									) : price > 0 ? (
										<>
											$
											<AnimatedNumber
												value={price}
												decimals={k === "ICP" ? 2 : 0}
												size="xs"
											/>
										</>
									) : (
										"—"
									)}
								</span>
							</span>
						);
					})}
				</div>
				<Link
					to={ROUTES.oracle}
					className="inline-flex shrink-0 items-center gap-0.5 text-[10px] font-semibold text-muted-foreground hover:text-foreground"
				>
					<span className="hidden sm:inline">Oracle</span>
					<ArrowRight className="size-3" />
				</Link>
			</div>
		</div>
	);
}
